import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  LogOut,
  Users,
  TrendingUp,
  CheckCircle2,
  Clock,
  Trash2,
  Eye,
  X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed", "archived"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const token = localStorage.getItem("jk_token");
  const user = JSON.parse(localStorage.getItem("jk_user") || "null");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [navigate, token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers });
      toast.success("Status updated");
      load();
      setActive(null);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers });
      toast.success("Lead deleted");
      load();
      setActive(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  const filtered =
    filter === "all" ? leads : leads.filter((l) => l.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream grid place-items-center">
        <p className="text-brand-muted">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
      <header className="bg-brand-forest text-brand-cream sticky top-0 z-30">
        <div className="container-app flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-brand-gold text-brand-forest font-bold">
              JK
            </span>
            <span className="font-bold">Admin Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-brand-cream/70 hidden sm:block">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:text-brand-gold transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-app py-10">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-forest">
          Lead Management
        </h1>
        <p className="mt-2 text-brand-muted">
          Review, prioritize, and update incoming leads.
        </p>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total Leads"
              value={stats.total}
              icon={Users}
              testid="stat-total"
            />
            <StatCard
              label="New"
              value={stats.new}
              icon={Clock}
              testid="stat-new"
              accent="gold"
            />
            <StatCard
              label="Contacted"
              value={stats.contacted}
              icon={TrendingUp}
              testid="stat-contacted"
            />
            <StatCard
              label="Closed"
              value={stats.closed}
              icon={CheckCircle2}
              testid="stat-closed"
            />
          </div>
        )}

        {/* Filters */}
        <div className="mt-10 flex items-center gap-2 flex-wrap">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${
                filter === s
                  ? "bg-brand-forest text-brand-cream"
                  : "bg-white border border-brand-line text-brand-muted hover:text-brand-forest"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-md border border-brand-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-sage/40 text-brand-forest text-left">
                <tr>
                  <th className="px-5 py-4 font-heading font-semibold">Name</th>
                  <th className="px-5 py-4 font-heading font-semibold">Program</th>
                  <th className="px-5 py-4 font-heading font-semibold">Phone</th>
                  <th className="px-5 py-4 font-heading font-semibold">Property</th>
                  <th className="px-5 py-4 font-heading font-semibold">Status</th>
                  <th className="px-5 py-4 font-heading font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-12 text-center text-brand-muted"
                    >
                      No leads in this view.
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      className="border-t border-brand-line hover:bg-brand-sage/20"
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-brand-forest">{l.name}</p>
                        <p className="text-xs text-brand-muted">{l.email}</p>
                      </td>
                      <td className="px-5 py-4 capitalize text-brand-muted">
                        {l.program.replace("-", " ")}
                      </td>
                      <td className="px-5 py-4 text-brand-muted">{l.phone}</td>
                      <td className="px-5 py-4 text-brand-muted truncate max-w-xs">
                        {l.property_address}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={l.status} />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setActive(l)}
                            data-testid={`view-lead-${l.id}`}
                            className="grid h-8 w-8 place-items-center rounded-md hover:bg-brand-sage/40 text-brand-forest"
                            aria-label="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => remove(l.id)}
                            data-testid={`delete-lead-${l.id}`}
                            className="grid h-8 w-8 place-items-center rounded-md hover:bg-red-50 text-red-600"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {active && (
        <div
          data-testid="lead-detail-modal"
          className="fixed inset-0 z-50 bg-brand-forest/40 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-brand-cream max-w-2xl w-full rounded-md p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="overline mb-2">{active.program.replace("-", " ")}</p>
                <h3 className="font-heading text-2xl font-bold text-brand-forest">
                  {active.name}
                </h3>
              </div>
              <button
                onClick={() => setActive(null)}
                className="grid h-9 w-9 place-items-center rounded-md hover:bg-brand-sage/40"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <dl className="space-y-3 text-sm">
              <Field label="Email" value={active.email} />
              <Field label="Phone" value={active.phone} />
              <Field label="Property Address" value={active.property_address} />
              <Field label="Property Condition" value={active.property_condition} />
              <Field label="Mortgage Status" value={active.mortgage_status} />
              <Field label="Timeline" value={active.timeline} />
              <Field label="Situation" value={active.situation} multiline />
              <Field
                label="Submitted"
                value={new Date(active.created_at).toLocaleString()}
              />
            </dl>

            <div className="mt-8 pt-6 border-t border-brand-line">
              <p className="text-sm font-semibold text-brand-forest mb-3">
                Update Status
              </p>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(active.id, s)}
                    data-testid={`set-status-${s}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${
                      active.status === s
                        ? "bg-brand-forest text-brand-cream"
                        : "bg-white border border-brand-line text-brand-muted hover:text-brand-forest"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, testid, accent }) => (
  <div
    data-testid={testid}
    className={`p-5 rounded-md border ${
      accent === "gold"
        ? "bg-brand-gold/10 border-brand-gold/30"
        : "bg-white border-brand-line"
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-brand-muted font-semibold">
        {label}
      </span>
      <Icon className="h-4 w-4 text-brand-forest" />
    </div>
    <p className="font-heading text-3xl font-bold text-brand-forest">{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-brand-gold/20 text-brand-gold-deep border-brand-gold/40",
    contacted: "bg-blue-50 text-blue-700 border-blue-200",
    qualified: "bg-purple-50 text-purple-700 border-purple-200",
    closed: "bg-brand-forest/10 text-brand-forest border-brand-forest/30",
    archived: "bg-brand-line text-brand-muted border-brand-line",
  };
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-md border text-xs font-semibold capitalize ${
        map[status] || map.new
      }`}
    >
      {status}
    </span>
  );
};

const Field = ({ label, value, multiline }) => (
  <div className="grid grid-cols-3 gap-4 py-2 border-b border-brand-line/50">
    <dt className="text-brand-muted">{label}</dt>
    <dd
      className={`col-span-2 text-brand-forest ${
        multiline ? "whitespace-pre-wrap" : ""
      }`}
    >
      {value || <span className="text-brand-muted/60">—</span>}
    </dd>
  </div>
);
